import { Text } from '../typography';
import styles from './Dropdown.module.scss';
import Select from 'react-select';
import SimpleWorkspaceCard from '../SimpleWorkspaceCard';
import Icon from '../Icon';
import VehicleOption from './VehicleOption';
import TypeOption from './TypeOption';
import { customColors } from '../../utilities';
import { useState, useMemo } from 'react';
import SimpleUserCard from '../SimpleUserCard';
import TextWithIcon from '../typography/TextWithIcon';
import { keys } from '../../utilities/translator/translation_keys';
import { useTranslation } from 'react-i18next';

export default function Dropdown({
  selected = null,
  onChange = () => {},
  options = [],
  isMulti = false,
  isClearable = false,
  keepOptionsOpen = false,
  type = null,
  label = null,
  isRequired = false,
  readOnly = false,
  placeholder,
  className,
  weight = 'semiBold',
}) {
  const { t } = useTranslation();
  const [focused, setFocused] = useState(false);

  // Stable component references are required for react-select. Defining components
  // inside the render function gives new references each render, which causes
  // react-select to unmount/remount its internal tree and can corrupt the menu
  // portal's DOM node, producing a removeChild error on document.body.
  const ListOption = useMemo(
    () =>
      function ListOption({ innerProps, data }) {
        switch (type) {
          case 'user':
            return (
              <div {...innerProps} className={styles.listOption}>
                <SimpleUserCard user={data} disableClick />
              </div>
            );
          case 'workspace':
          case 'workspace_not_null':
            return (
              <div {...innerProps} className={styles.listOption}>
                <SimpleWorkspaceCard workspace={data} />
              </div>
            );
          case 'asset':
            return (
              <div {...innerProps} className={styles.listOption}>
                <VehicleOption vehicle={data} />
              </div>
            );
          case 'templateType':
            return (
              <div {...innerProps} className={styles.listOption}>
                <TypeOption type={data} />
              </div>
            );
          case 'template':
            return (
              <div {...innerProps} className={styles.listOption}>
                <Text noMargin weight="semiBold">
                  {data?.title}
                </Text>
                {!data?.workspace ? (
                  <Text noMargin size="xs" color="secondary">
                    {t(keys.common.SHARED)}
                  </Text>
                ) : (
                  <Text noMargin size="xs" color="secondary">
                    {data?.workspace?.title}
                  </Text>
                )}
              </div>
            );
          default:
            return (
              <div {...innerProps} className={styles.listOption}>
                <TextWithIcon
                  leftIcon={data.icon}
                  noMargin
                  weight={weight}
                  iconProps={{ color: data.color || 'black' }}
                >
                  {data?.label || data}
                </TextWithIcon>
              </div>
            );
        }
      },
    [type, weight, t],
  );

  const MultiSelectedOption = useMemo(
    () =>
      function MultiSelectedOption({ innerProps, data, removeProps }) {
        let optionElement = null;
        switch (type) {
          case 'workspace':
          case 'workspace_not_null':
            optionElement = (
              <SimpleWorkspaceCard
                workspace={data}
                weight="regular"
                truncate={false}
              />
            );
            break;
          case 'user':
            optionElement = <SimpleUserCard user={data} disableClick />;
            break;
          default:
            optionElement = <VehicleOption vehicle={data} />;
            break;
        }
        return (
          <div {...innerProps} className={styles.option}>
            {optionElement}
            {!readOnly ? (
              <Icon
                {...removeProps}
                className={styles.closeIcon}
                style={{ fontSize: '1rem' }}
              >
                close
              </Icon>
            ) : (
              <div> </div>
            )}
          </div>
        );
      },
    [type, readOnly],
  );

  const SingleSelectedOption = useMemo(
    () =>
      function SingleSelectedOption({ data }) {
        if (focused) return <div className={styles.hide} />;
        switch (type) {
          case 'workspace':
          case 'workspace_not_null':
            return <SimpleWorkspaceCard workspace={data} weight="regular" />;
          case 'asset':
            return <VehicleOption vehicle={data} />;
          case 'templateType':
            return <TypeOption type={data} />;
          case 'template':
            return (
              <Text noMargin weight="semiBold">
                {data?.title ?? 'Something'}
              </Text>
            );
          case 'user':
            return <SimpleUserCard user={data} disableClick />;
          default:
            return (
              <TextWithIcon
                leftIcon={data.icon}
                noMargin
                weight={weight}
                iconProps={{ color: data.color || 'black' }}
              >
                {data?.label || data}
              </TextWithIcon>
            );
        }
      },
    [focused, type, weight],
  );

  const placeholderContent = useMemo(() => {
    if (focused) return <div className={styles.hide} />;
    switch (type) {
      case 'workspace':
      case 'workspace_not_null':
        return (
          <div className={styles.placeholder}>
            <Icon>list_alt</Icon>
            <Text noMargin>{placeholder ?? 'Select Workspace'}</Text>
          </div>
        );
      case 'asset':
        return (
          <div className={styles.placeholder}>
            <Icon>local_shipping</Icon>
            <Text noMargin>{placeholder ?? 'Select Asset'}</Text>
          </div>
        );
      case 'template':
        return (
          <div className={styles.placeholder}>
            <Icon>text_snippet</Icon>
            <Text noMargin>{placeholder ?? 'Select Template'}</Text>
          </div>
        );
      case 'user':
        return (
          <div className={styles.placeholder}>
            <Icon>person</Icon>
            <Text noMargin>{placeholder ?? `Select ${label ?? 'User'}`}</Text>
          </div>
        );
      default:
        return (
          <div className={styles.placeholder}>
            <Text noMargin>{`Select ${placeholder || '...'}`}</Text>
          </div>
        );
    }
  }, [focused, type, placeholder, label]);

  const getOptionLabel = (o) => {
    switch (type) {
      case 'asset':
        return o.unitNumber;
      case 'templateType':
        return o.name;
      case 'workspace':
      case 'workspace_not_null':
      case 'template':
        return o.title;
      case 'user':
        return `${o.firstName} ${o.lastName}`;
      default:
        return o;
    }
  };

  const formattedOptions =
    options?.map((o) => (typeof o === 'string' ? { label: o, value: o } : o)) ||
    [];

  const sharedWorkspaceOption = {
    label: t(keys.common.SHARED),
    icon: 'public',
    color: customColors.BLUE,
    value: null,
    title: t(keys.common.SHARED),
    id: null,
  };

  return (
    <div className={className}>
      <Select
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        blurInputOnSelect={!keepOptionsOpen}
        isDisabled={readOnly}
        placeholder={placeholderContent}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{
          control: (base) => ({
            ...base,
            borderRadius: '8px',
            transition: 'none',
            borderColor: isRequired
              ? 'rgba(227, 59, 59, 0.5)'
              : customColors.SECONDARY_LIGHT,
          }),
          valueContainer: (base) => ({
            ...base,
            display: 'flex',
            gap: '0.3rem',
            flexWrap: 'wrap',
            minHeight: '2.5rem',
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollbarGutter: 'stable',
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 2147483647,
          }),
          menu: (base) => ({
            ...base,
            transform: 'translateY(-6px)',
          }),
        }}
        isMulti={isMulti}
        isClearable={isClearable}
        closeMenuOnSelect={!keepOptionsOpen}
        isSearchable={true}
        options={
          type === 'workspace'
            ? [sharedWorkspaceOption, ...formattedOptions]
            : formattedOptions
        }
        value={
          type === 'workspace' && !selected ? sharedWorkspaceOption : selected
        }
        getOptionValue={(o) => o?.id ?? o}
        getOptionLabel={getOptionLabel}
        onChange={onChange}
        components={{
          Option: ListOption,
          MultiValue: MultiSelectedOption,
          SingleValue: SingleSelectedOption,
        }}
      />
    </div>
  );
}
